import { useState, useCallback } from 'react'
import {
    IconMessageCircle,
    IconBug,
    IconBulb,
    IconPalette,
    IconQuestionMark,
    IconSend,
    IconCheck,
} from '@tabler/icons-react'
import { useApiClient } from '@/lib/api-client'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/lilia-ui/dialog'
import { Button } from '@/components/lilia-ui/button'

// Feedback surface redesign (2026-04-19): the old fixed bottom-right fab got
// chronically covered by editor overlays (ActivityBar panels, preview panes,
// toolbars). Now it's a persistent header button + centered modal. Visible at
// every breakpoint, no z-index fights with floating panels.

type FeedbackType = 'bug' | 'feature' | 'ux' | 'question'

const FEEDBACK_TYPES: { value: FeedbackType; label: string; icon: typeof IconBug }[] = [
    { value: 'bug', label: 'Bug', icon: IconBug },
    { value: 'feature', label: 'Feature', icon: IconBulb },
    { value: 'ux', label: 'UX', icon: IconPalette },
    { value: 'question', label: 'Question', icon: IconQuestionMark },
]

function getDocumentIdFromUrl(): string | null {
    const path = window.location.pathname
    // /d/:id is the editor's address; /studio and /document are the older ones.
    const match = path.match(/\/(d|studio|document)\/([^/]+)/)
    return match ? match[2] : null
}

/**
 * Compact button that opens the Feedback modal. Designed to sit in a toolbar
 * or header bar. Icon-only at tight widths, icon + label at sm+.
 */
export function FeedbackButton({ compact = false }: { compact?: boolean } = {}) {
    const [isOpen, setIsOpen] = useState(false)

    return (
        <>
            <button
                onClick={() => setIsOpen(true)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-bg)] hover:text-[var(--lilia-text)] transition-colors"
                title="Send feedback"
                aria-label="Send feedback"
            >
                <IconMessageCircle size={16} />
                {!compact && <span className="hidden md:inline text-xs font-medium">Feedback</span>}
            </button>
            <FeedbackDialog open={isOpen} onClose={() => setIsOpen(false)} />
        </>
    )
}

/**
 * Feedback dialog — centered modal, no overlap with editor chrome. Also
 * exportable so other surfaces (e.g. review review-page, onboarding) can
 * pop it without wiring their own trigger.
 */
export function FeedbackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [type, setType] = useState<FeedbackType | null>(null)
    const [message, setMessage] = useState('')
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
    const { fetchWithAuth } = useApiClient()

    const reset = useCallback(() => {
        setType(null)
        setMessage('')
        setStatus('idle')
    }, [])

    const handleClose = useCallback(() => {
        reset()
        onClose()
    }, [reset, onClose])

    async function handleSubmit() {
        if (!type || !message.trim()) return
        setStatus('submitting')
        const body = {
            type,
            message: message.trim(),
            page: window.location.pathname,
            documentId: getDocumentIdFromUrl(),
            metadata: {
                userAgent: navigator.userAgent,
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                timestamp: new Date().toISOString(),
            },
        }
        try {
            const response = await fetchWithAuth('/api/feedback', {
                method: 'POST',
                body: JSON.stringify(body),
            })
            if (!response.ok) throw new Error('Failed to submit feedback')
            setStatus('success')
            setTimeout(handleClose, 1500)
        } catch {
            setStatus('error')
            setTimeout(() => setStatus('idle'), 3000)
        }
    }

    return (
        <Dialog open={open} onOpenChange={(o) => !o && status !== 'submitting' && handleClose()}>
            <DialogContent className="max-w-[480px]" data-testid="feedback-dialog" data-status={status}>
                <DialogHeader>
                    <DialogTitle>Send feedback</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 p-2">
                    {/* Type picker */}
                    <div className="grid grid-cols-4 gap-2">
                        {FEEDBACK_TYPES.map((ft) => {
                            const Icon = ft.icon
                            const selected = type === ft.value
                            return (
                                <button
                                    key={ft.value}
                                    data-testid="feedback-type"
                                    data-type={ft.value}
                                    onClick={() => setType(ft.value)}
                                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-lg border transition-colors ${
                                        selected
                                            ? 'border-[var(--lilia-primary,#4f46e5)] bg-[var(--lilia-primary,#4f46e5)]/5'
                                            : 'border-[var(--lilia-border,#e5e7eb)] hover:border-[var(--lilia-primary,#4f46e5)]/40'
                                    }`}
                                    aria-pressed={selected}
                                >
                                    <Icon size={20} className={selected ? 'text-[var(--lilia-primary,#4f46e5)]' : ''} />
                                    <span className="text-[11px] font-medium">{ft.label}</span>
                                </button>
                            )
                        })}
                    </div>

                    {/* Message */}
                    <textarea
                        data-testid="feedback-message"
                        value={message}
                        onChange={(e) => setMessage(e.currentTarget.value)}
                        placeholder={
                            type === 'bug'
                                ? 'What broke? Steps to reproduce if you have them.'
                                : type === 'feature'
                                    ? 'What would you like Lilia to do?'
                                    : type === 'ux'
                                        ? 'What feels off or could be clearer?'
                                        : type === 'question'
                                            ? "What aren't you sure about?"
                                            : 'Pick a category above, then tell us more.'
                        }
                        rows={5}
                        className="w-full rounded border border-[var(--lilia-border)] px-3 py-2 text-[13px] leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-[var(--lilia-primary,#4f46e5)]"
                        disabled={!type || status === 'submitting'}
                    />

                    {status === 'error' && (
                        <div data-testid="feedback-error" className="rounded bg-rose-50 border border-rose-200 px-3 py-2 text-[13px] text-rose-700">
                            Couldn't send. Try again.
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" data-testid="feedback-cancel" onClick={handleClose} disabled={status === 'submitting'}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        data-testid="feedback-submit"
                        onClick={handleSubmit}
                        disabled={!type || !message.trim() || status === 'submitting' || status === 'success'}
                    >
                        {status === 'submitting' && <>Sending…</>}
                        {status === 'success' && (
                            <>
                                <IconCheck size={16} className="mr-2" />
                                Thank you!
                            </>
                        )}
                        {(status === 'idle' || status === 'error') && (
                            <>
                                <IconSend size={16} className="mr-2" />
                                Submit
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

// Backwards-compatible default export — any existing callers importing the
// old floating FeedbackWidget now get the button-only variant instead.
function FeedbackWidget() {
    return <FeedbackButton />
}

export default FeedbackWidget
